import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { useAuthStore } from './authStore'

export const useProjectStore = defineStore('project', () => {
  const projects = ref([])
  const currentProject = ref(null)
  const loading = ref(false)
  const alerts = ref([])
  const checkpoints = ref([])

  // MOCK data uses status 'in_progress'; include all non-archived projects
  const activeProjects = computed(() =>
    projects.value.filter(p => p.status !== 'archived' && p.status !== 'completed')
  )

  // Filter projects by current user's hashid
  const userProjects = computed(() => {
    const authStore = useAuthStore()
    if (!authStore.userHashId) return projects.value
    return projects.value.filter(p => p.owner_hashid === authStore.userHashId)
  })

  const getProjects = async () => {
    loading.value = true
    try {
      const response = await api.get('/projects/')
      projects.value = response.data.results || response.data
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    } finally {
      loading.value = false
    }
  }

  const getProject = async (id) => {
    try {
      const response = await api.get(`/projects/${id}/`)
      currentProject.value = response.data
      return response.data
    } catch (error) {
      console.error('Failed to fetch project:', error)
    }
  }

  const createProject = async (projectData) => {
    try {
      const response = await api.post('/projects/', projectData)
      projects.value.push(response.data)
      return response.data
    } catch (error) {
      console.error('Failed to create project:', error)
      throw error
    }
  }

  const updateProject = async (id, projectData) => {
    try {
      const response = await api.patch(`/projects/${id}/`, projectData)
      const index = projects.value.findIndex(p => p.id === id)
      if (index !== -1) {
        projects.value[index] = response.data
      }
      return response.data
    } catch (error) {
      console.error('Failed to update project:', error)
      throw error
    }
  }

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}/`)
      const index = projects.value.findIndex(p => p.id === id)
      if (index !== -1) {
        projects.value.splice(index, 1)
      }
    } catch (error) {
      console.error('Failed to delete project:', error)
      throw error
    }
  }

  const getAlerts = async (projectId) => {
    try {
      const response = await api.get('/alerts/')
      alerts.value = response.data.results || response.data
    } catch (error) {
      console.error('Failed to fetch alerts:', error)
    }
  }

  const getCheckpoints = async (phaseId) => {
    try {
      const response = await api.get('/checkpoints/')
      checkpoints.value = response.data.results || response.data
    } catch (error) {
      console.error('Failed to fetch checkpoints:', error)
    }
  }

  const generateTrustPassport = async (projectId) => {
    try {
      const response = await api.post(`/projects/${projectId}/generate_trust_passport/`)
      return response.data
    } catch (error) {
      console.error('Failed to generate trust passport:', error)
      throw error
    }
  }

  return {
    projects,
    currentProject,
    loading,
    alerts,
    checkpoints,
    activeProjects,
    userProjects,
    getProjects,
    getProject,
    createProject,
    updateProject,
    deleteProject,
    getAlerts,
    getCheckpoints,
    generateTrustPassport
  }
})
